import api from './api';

export interface VideoScript {
  id: number;
  title: string;
  idea: string;

  mode: 'HALF_NARRATION' | 'PURE_NARRATION' | string;

  /** episode script output format */
  scriptFormat?: 'SCENE' | 'STORYBOARD' | 'NARRATION' | string;

  /** per-episode settings */
  targetSeconds: number;
  sceneCount: number;

  /** series workflow outputs */
  scriptSetting?: string;
  outline?: string;
  hooksJson?: string;
  prologue?: string;

  /** legacy single-episode outputs (kept for backward compatibility) */
  storyboard?: string;
  finalScript?: string;

  /** multi-episode controls */
  episodeCount?: number;
  currentEpisode?: number;
  currentRetryCount?: number;
  maxRetryPerEpisode?: number;
  minPassScore?: number;
  enableOutlineUpdate?: boolean;

  activeStep?: string;
  workflowConfig?: string;

  status: string;
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export interface VideoScriptEpisode {
  id: number;
  scriptId: number;
  episodeNumber: number;
  title: string;
  brief?: string;
  lastAdjustment?: string;
  storyboard?: string;
  content?: string;
  wordCount?: number;
  status: string;
  reviewResult?: string;
  analysisResult?: string;
  generationTime?: number;
  createdAt: string;
  updatedAt: string;
}

export interface VideoScriptLog {
  id: number;
  type: 'INFO' | 'THOUGHT' | 'ACTION' | 'REVIEW' | 'ERROR' | 'SUCCESS' | string;
  content: string;
  episodeNumber?: number | null;
  createdAt: string;
}

export type WorkflowStepStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';

export interface WorkflowStep {
  key: string;
  name: string;
  status: WorkflowStepStatus;
  episodeNumber?: number | null;
  description?: string;
}

export interface VideoScriptWorkflowStateResponse {
  scriptId: number;
  status: string;
  activeStep?: string | null;
  episodeCount?: number;
  currentEpisode?: number;
  steps: WorkflowStep[];
}

/** 审稿雷达七维 key（与后端/审稿模板评分雷达统一） */
export type QualityRadarDimension =
  | 'goal'
  | 'hook'
  | 'pacing'
  | 'logic'
  | 'foreshadow'
  | 'continuity'
  | 'expression';

/** 质量报告：低分集条目（score<7 或带警告通过的集，按分升序） */
export interface QualityReportLowScoreEpisode {
  episodeNumber: number;
  score: number;
  forcedPass: boolean;
  comments?: string;
  suggestions?: string;
}

/** 质量报告：全集概览条目 */
export interface QualityReportEpisode {
  episodeNumber: number;
  score: number;
  passed: boolean;
  forcedPass: boolean;
  retryCount: number;
}

/** 剧本质量报告（GET /video-scripts/{id}/quality-report 的 data 结构，前后端共同契约） */
export interface VideoScriptQualityReport {
  scriptId: number;
  episodeTotal: number;
  reviewedCount: number;
  avgScore: number;
  firstPassRate: number;
  forcedPassCount: number;
  forcedPassByReason: Record<string, number>;
  /** 只统计带 radar 字段的集；旧数据可能为空对象 */
  radarAvg: Partial<Record<QualityRadarDimension, number>>;
  radarSampleCount: number;
  weakestDimensions: QualityRadarDimension[];
  lowScoreEpisodes: QualityReportLowScoreEpisode[];
  episodes: QualityReportEpisode[];
}

export const videoScriptService = {
  create: async (data: any) => {
    return api.post<VideoScript>('/video-scripts', data);
  },

  list: async () => {
    return api.get<VideoScript[]>('/video-scripts');
  },

  get: async (id: number) => {
    return api.get<VideoScript>(`/video-scripts/${id}`);
  },

  start: async (id: number) => {
    return api.post(`/video-scripts/${id}/start`);
  },

  pause: async (id: number) => {
    return api.post(`/video-scripts/${id}/pause`);
  },

  getLogs: async (id: number, params?: { episodeNumber?: number; page?: number; size?: number }) => {
    const page = params?.page ?? 0;
    const size = params?.size ?? 50;
    const episodeNumber = params?.episodeNumber;
    const epPart = typeof episodeNumber === 'number' ? `&episodeNumber=${episodeNumber}` : '';
    return api.get<VideoScriptLog[]>(`/video-scripts/${id}/logs?page=${page}&size=${size}${epPart}`);
  },

  getEpisodes: async (id: number) => {
    return api.get<VideoScriptEpisode[]>(`/video-scripts/${id}/episodes`);
  },

  updateEpisodeContent: async (id: number, episodeNumber: number, content: string) => {
    return api.put<VideoScriptEpisode>(`/video-scripts/${id}/episodes/${episodeNumber}/content`, { content });
  },

  retry: async (id: number, episodeNumber: number) => {
    return api.post(`/video-scripts/${id}/retry/${episodeNumber}`);
  },

  getWorkflowState: async (id: number) => {
    return api.get<VideoScriptWorkflowStateResponse>(`/video-scripts/${id}/workflow/state`);
  },

  getQualityReport: async (id: number) => {
    return api.get<VideoScriptQualityReport>(`/video-scripts/${id}/quality-report`);
  },

  updateConfig: async (id: number, config: { modelId?: string; scriptFormat?: 'SCENE' | 'STORYBOARD' | 'NARRATION' | string }) => {
    return api.put(`/video-scripts/${id}/config`, config);
  },
};
